// src/common/ToastSystem.jsx - With Inline SVG Icons
import React, { createContext, useContext, useState, useCallback } from 'react';

// Toast Context
const ToastContext = createContext();

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      message,
      type,
      duration,
      timestamp: Date.now()
    };

    setToasts(prev => [...prev, toast]);

    // Auto remove toast after duration
    setTimeout(() => {
      removeToast(id);
    }, duration);

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const value = {
    toasts,
    addToast,
    removeToast,
    // Convenience methods
    success: (message, duration) => addToast(message, 'success', duration),
    error: (message, duration) => addToast(message, 'error', duration),
    warning: (message, duration) => addToast(message, 'warning', duration),
    info: (message, duration) => addToast(message, 'info', duration),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

// Custom hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Individual Toast Component
const Toast = ({ toast, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  React.useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onClose(toast.id), 300);
  };

  const getIcon = (type) => {
    const iconColor = type === 'success' ? '#198754' :
                     type === 'error' ? '#dc3545' :
                     type === 'warning' ? '#ffc107' : '#106cbd';
    
    switch(type) {
      case 'success':
        return (
          <svg width="20" height="20" fill={iconColor} viewBox="0 0 16 16">
            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.061L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
          </svg>
        );
      case 'error':
        return (
          <svg width="20" height="20" fill={iconColor} viewBox="0 0 16 16">
            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
          </svg>
        );
      case 'warning':
        return (
          <svg width="20" height="20" fill={iconColor} viewBox="0 0 16 16">
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
          </svg>
        );
      case 'info':
      default:
        return (
          <svg width="20" height="20" fill={iconColor} viewBox="0 0 16 16">
            <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
          </svg>
        );
    }
  };

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  return (
    <>
      <style jsx>{`
        @keyframes progress {
          to { right: 100%; }
        }
      `}</style>
      
      <div 
        className={`custom-toast custom-toast-${toast.type} ${isVisible && !isExiting ? 'active' : ''}`}
        style={{
          minWidth: '260px',
          padding: '8px 24px 8px 12px',
          borderRadius: '8px',
          background: '#fff',
          boxShadow: 'rgb(0 0 0 / 43%) 0px 4px 14px -4px',
          opacity: isVisible && !isExiting ? 1 : 0,
          transform: isVisible && !isExiting ? 'translateY(0)' : 'translateY(-20px)',
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          marginBottom: '8px',
          fontFamily: 'Raleway, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div className={`toast-icon toast-icon-${toast.type}`}>
            {getIcon(toast.type)}
          </div>
          <div style={{ flex: 1 }}>
            <div className="toast-title" style={{ 
              fontSize: '12px', 
              fontWeight: '600', 
              marginBottom: '2px'
            }}>
              {capitalize(toast.type)}
            </div>
            <div className="toast-message" style={{ 
              fontSize: '11px', 
              opacity: '0.7'
            }}>
              {toast.message}
            </div>
          </div>
        </div>
        
        <button 
          className="toast-close-btn"
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '4px',
            right: '8px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            opacity: '0.7',
            fontSize: '16px',
            padding: '0',
            width: '16px',
            height: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'opacity 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.7'}
        >
          ×
        </button>
        
        <div 
          className="toast-progress"
          style={{
            position: 'absolute',
            bottom: '0',
            left: '0',
            height: '2px',
            width: '100%',
            overflow: 'hidden'
          }}
        >
          <div 
            className="toast-progress-bar"
            style={{
              position: 'absolute',
              bottom: '0',
              right: '0',
              width: '100%',
              height: '100%',
              background: toast.type === 'success' ? '#198754' :
                          toast.type === 'error' ? '#dc3545' :
                          toast.type === 'warning' ? '#ffc107' : '#106cbd',
              animation: `progress ${toast.duration}ms linear forwards`
            }}
          />
        </div>
      </div>
    </>
  );
};

// Toast Container Component
const ToastContainer = () => {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div 
      className="toast-container"
      style={{
        position: 'fixed',
        top: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pointerEvents: 'none'
      }}
    >
      {toasts.map((toast) => (
        <div key={toast.id} style={{ pointerEvents: 'auto' }}>
          <Toast toast={toast} onClose={removeToast} />
        </div>
      ))}
    </div>
  );
};